import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, Save, Edit, Settings, ShieldCheck, AlertTriangle, X,
  Briefcase, Package, Euro, TrendingUp, FileText, Truck,
} from 'lucide-react';

const fmtMoney = (n) => n == null ? '-' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

const BADGE_STYLES = {
  green:   { box: 'bg-emerald-50 border-emerald-300', text: 'text-emerald-700', icon: ShieldCheck,    pillBg: 'bg-emerald-600' },
  orange:  { box: 'bg-amber-50 border-amber-300',     text: 'text-amber-700',   icon: AlertTriangle,  pillBg: 'bg-amber-500' },
  red:     { box: 'bg-red-50 border-red-300',         text: 'text-red-700',     icon: AlertTriangle,  pillBg: 'bg-red-600' },
  neutral: { box: 'bg-slate-50 border-slate-200',     text: 'text-slate-600',   icon: Settings,       pillBg: 'bg-slate-400' },
};

export default function RentabiliteWidget({ devisId }) {
  const [lifecycle, setLifecycle] = useState(null);
  const [sousTraitants, setSousTraitants] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [form, setForm] = useState({
    sous_traitant_id: '', sous_traitant_nom: '',
    cout_main_oeuvre: '', cout_materiaux: '', cout_autres: '',
    notes_chantier: '',
  });

  const reload = async () => {
    if (!devisId) return;
    try {
      const r = await api.get(`/devis/${devisId}/lifecycle`);
      setLifecycle(r.data);
      const lc = r.data.lifecycle || {};
      setForm({
        sous_traitant_id: lc.sous_traitant_id || '',
        sous_traitant_nom: lc.sous_traitant_nom || '',
        cout_main_oeuvre: lc.cout_main_oeuvre ?? '',
        cout_materiaux: lc.cout_materiaux ?? '',
        cout_autres: lc.cout_autres ?? '',
        notes_chantier: lc.notes_chantier || '',
      });
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    reload();
    api.get('/sous-traitants-selector')
      .then((r) => setSousTraitants(r.data.sous_traitants || []))
      .catch(() => {});
  }, [devisId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/devis/${devisId}/lifecycle`, {
        sous_traitant_id: form.sous_traitant_id,
        sous_traitant_nom: form.sous_traitant_nom,
        cout_main_oeuvre: parseFloat(form.cout_main_oeuvre) || 0,
        cout_materiaux: parseFloat(form.cout_materiaux) || 0,
        cout_autres: parseFloat(form.cout_autres) || 0,
        notes_chantier: form.notes_chantier,
      });
      await reload();
      setEditing(false);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (!lifecycle) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const fin = lifecycle.financials || {};
  const rent = lifecycle.rentabilite || {};
  const badge = rent.badge_color || 'neutral';
  const bs = BADGE_STYLES[badge];
  const BadgeIcon = bs.icon;

  return (
    <div className="space-y-4" data-testid="rentabilite-widget">
      {/* 5 KPI compactes */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KPI label="Devis HT"        value={fmtMoney(fin.montant_devis_ht)} icon={FileText}  color="slate" />
        <KPI label="Main d'œuvre S/T" value={fmtMoney(fin.cout_main_oeuvre)} icon={Briefcase} color="orange" />
        <KPI label="Matériaux"        value={fmtMoney(fin.cout_materiaux)}   icon={Package}   color="blue" />
        <KPI label="Coût total réel"  value={fmtMoney(fin.cout_total_reel)}  icon={Euro}      color="red" />
        <KPI label="Marge nette"
             value={`${fmtMoney(fin.marge_nette)}${fin.marge_percent != null ? ` (${fin.marge_percent}%)` : ''}`}
             icon={TrendingUp} color={(fin.marge_nette ?? 0) >= 0 ? 'emerald' : 'red'} highlight />
      </div>

      {/* Décision rentabilité */}
      <div className={`rounded-xl border-2 ${bs.box} p-4`} data-testid="rentabilite-card">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bs.pillBg} text-white shrink-0`}>
            <BadgeIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Décision rentabilité</p>
                <p className={`text-base font-bold ${bs.text}`} data-testid="rentabilite-badge">{rent.badge_label || '—'}</p>
              </div>
              <button onClick={() => setShowSettings(true)}
                className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="open-finance-settings-btn">
                <Settings className="w-3.5 h-3.5" /> Paramètres
              </button>
            </div>
            <p className="text-sm text-slate-700 mt-1">{rent.message}</p>
            {rent.coef_charges_pourcent != null && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 pt-3 border-t border-slate-200/60">
                <MiniStat label="Quote-part charges fixes" value={fmtMoney(rent.quote_part_charges_fixes)} hint={`${rent.coef_charges_pourcent}% du HT`} />
                <MiniStat label="Coût total + charges"      value={fmtMoney(rent.cout_total_avec_charges)} />
                <MiniStat label="Marge nette finale"        value={fmtMoney(rent.marge_nette_finale)} hint={`${rent.marge_percent_finale ?? 0}%`} highlight={badge} />
                <MiniStat label="Cible / Seuil"             value={`${rent.marge_cible}% / ${rent.marge_seuil_orange}%`} hint="objectif / plancher" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edition coûts */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
            <Truck className="w-4 h-4 text-orange-600" /> Coûts réels (sous-traitance & matériaux)
          </h3>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-costs-btn">
              <Edit className="w-3.5 h-3.5 mr-1" /> Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); reload(); }}>Annuler</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="save-costs-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Enregistrer
              </Button>
            </div>
          )}
        </div>
        <div className="p-4">
          {!editing ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
              <Row label="Sous-traitant"  value={form.sous_traitant_nom || '—'} />
              <Row label="Main d'œuvre"   value={fmtMoney(parseFloat(form.cout_main_oeuvre) || 0)} />
              <Row label="Matériaux"      value={fmtMoney(parseFloat(form.cout_materiaux) || 0)} />
              <Row label="Autres frais"   value={fmtMoney(parseFloat(form.cout_autres) || 0)} />
              {form.notes_chantier && <div className="md:col-span-4"><Row label="Notes" value={form.notes_chantier} /></div>}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sous-traitant</label>
                <select className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  value={form.sous_traitant_id}
                  onChange={(e) => {
                    const st = sousTraitants.find((s) => s.sous_traitant_id === e.target.value);
                    setForm({ ...form, sous_traitant_id: e.target.value, sous_traitant_nom: st?.nom || '' });
                  }}>
                  <option value="">— aucun —</option>
                  {sousTraitants.map((st) => <option key={st.sous_traitant_id} value={st.sous_traitant_id}>{st.nom}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <NumberField label="Main d'œuvre (€)" value={form.cout_main_oeuvre} onChange={(v) => setForm({ ...form, cout_main_oeuvre: v })} testid="cost-mo" />
                <NumberField label="Matériaux (€)"     value={form.cout_materiaux}    onChange={(v) => setForm({ ...form, cout_materiaux: v })}    testid="cost-mat" />
                <NumberField label="Autres (€)"        value={form.cout_autres}       onChange={(v) => setForm({ ...form, cout_autres: v })}       testid="cost-aut" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <Textarea rows={2} value={form.notes_chantier} onChange={(e) => setForm({ ...form, notes_chantier: e.target.value })} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showSettings && <FinanceSettingsDialog onClose={() => setShowSettings(false)} onSaved={reload} />}
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, highlight }) {
  const colors = {
    slate:'bg-slate-100 text-slate-700',
    orange:'bg-orange-100 text-orange-700',
    blue:'bg-blue-100 text-blue-700',
    red:'bg-red-100 text-red-700',
    emerald:'bg-emerald-100 text-emerald-700',
  };
  return (
    <div className={`rounded-lg p-3 border ${highlight ? `${colors[color]} border-2` : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1 rounded ${colors[color]}`}><Icon className="w-3 h-3" /></div>
        <span className="text-[10px] text-slate-500 truncate">{label}</span>
      </div>
      <p className="text-sm sm:text-base font-bold text-slate-900 leading-tight">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, hint, highlight }) {
  const colors = { green:'text-emerald-700', orange:'text-amber-700', red:'text-red-700', neutral:'text-slate-700' };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm font-bold ${colors[highlight] || 'text-slate-900'}`}>{value}</p>
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-slate-400">{label}</p>
      <p className="text-sm text-slate-900 font-medium">{value}</p>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <Input type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0" data-testid={testid} />
    </div>
  );
}

function FinanceSettingsDialog({ onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/company/finance').then((r) => setData(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put('/company/finance', {
        charges_fixes_annuelles: parseFloat(data.charges_fixes_annuelles) || 0,
        ca_objectif_annuel: parseFloat(data.ca_objectif_annuel) || 0,
        marge_nette_cible: parseFloat(data.marge_nette_cible) || 0,
        marge_nette_seuil_orange: parseFloat(data.marge_nette_seuil_orange) || 0,
      });
      onSaved && onSaved();
      onClose();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="finance-settings-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-700" /> Paramètres financiers
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!data ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Servent à calculer la marge nette finale (avec quote-part de charges fixes) et le badge de décision sur chaque devis.</p>
            <NumberField label="Charges fixes annuelles (€)" value={data.charges_fixes_annuelles} onChange={(v) => setData({ ...data, charges_fixes_annuelles: v })} testid="settings-charges" />
            <NumberField label="CA objectif annuel (€)"      value={data.ca_objectif_annuel}      onChange={(v) => setData({ ...data, ca_objectif_annuel: v })}      testid="settings-ca" />
            <div className="grid grid-cols-2 gap-3">
              <NumberField label="Marge nette cible (%)"     value={data.marge_nette_cible}        onChange={(v) => setData({ ...data, marge_nette_cible: v })}        testid="settings-cible" />
              <NumberField label="Seuil orange (%)"          value={data.marge_nette_seuil_orange} onChange={(v) => setData({ ...data, marge_nette_seuil_orange: v })} testid="settings-orange" />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
              <div className="flex justify-between"><span>Coef charges fixes calculé</span><strong className="text-slate-900">{data.coef_charges_pourcent}%</strong></div>
              <div className="flex justify-between"><span>Coef vente plancher</span><strong className="text-slate-900">×{data.coef_vente_plancher ?? '—'}</strong></div>
              <p className="text-[11px] text-slate-500 mt-2 pt-2 border-t border-slate-200">
                <strong>Décision visuelle</strong> : <span className="text-emerald-700">vert</span> si marge ≥ cible, <span className="text-amber-700">orange</span> entre seuil et cible, <span className="text-red-700">rouge</span> sous le seuil.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
